import {useEffect, useRef} from "react";
import {type GraphQLSubscription} from "@aws-amplify/api-graphql";
import {useOktaAuth} from "@okta/okta-react";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {AmplifyClientKey, GraphqlVariables} from "./types";

interface UseGraphqlSubscriptionOptions<
  TData,
  TVariables extends GraphqlVariables
> {
  /** Amplify subscription string */
  amplifySubscription: string;
  /** Variables for the subscription */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Callback when data is received */
  onData?: (data: TData) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Amplify-only subscription hook.
 *
 * Uses client.graphql() which returns an Observable,
 * subscribed via useEffect with automatic cleanup on unmount.
 *
 * AppSync subscriptions use AWS_LAMBDA auth — the access token is passed
 * as the authToken so the WebSocket handshake authenticates correctly.
 */
export function useGraphqlSubscription<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(options: UseGraphqlSubscriptionOptions<TData, TVariables>): void {
  const {authState} = useOktaAuth();

  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  // Use refs for callbacks to avoid re-subscribing when callbacks change
  const onDataRef = useRef(options.onData);
  const onErrorRef = useRef(options.onError);
  onDataRef.current = options.onData;
  onErrorRef.current = options.onError;

  // Get the access token for Lambda auth on the WebSocket connection
  const accessToken = authState?.accessToken?.accessToken;

  useEffect(() => {
    if (!client || !accessToken) return;

    // AppSync subscriptions require Lambda auth with the access token.
    // Without authMode + authToken, the WebSocket connection silently fails.
    const observable = client.graphql<GraphQLSubscription<TData>>({
      query: options.amplifySubscription,
      variables: options.variables as any,
      authMode: "lambda",
      authToken: `Bearer ${accessToken}`
    } as any) as any as import("rxjs").Observable<{data: TData}>;

    const subscription = observable.subscribe({
      next: (message: {data: TData}) => {
        onDataRef.current?.(message.data);
      },
      error: (err: unknown) => {
        const error =
          err instanceof Error ? err : new Error(String(err));
        onErrorRef.current?.(error);
      }
    });

    return () => {
      subscription.unsubscribe();
    };

  }, [client, accessToken, options.amplifySubscription, JSON.stringify(options.variables)]);
}
